// ENDLESS GOALS (owner 2026-11-02, 'endless mode goals'): milestones past the final boss, each a named TIER with an
// escalating shard bonus (banked at once: paid at the end of the run even if the hero falls later), an Apex Boon pick,
// and endless-only escalation (elite packs from tier II, twin guardians from tier III). Pure data + functions; the Sim
// (src/core/sim.ts) keeps the state. Endless time = real seconds since the final boss fell (sim.time - sim.victoryAt).

export interface EndlessTierDef { name: string; at: number; shards: number }
/** the named tiers (original names). at = endless seconds; shards = bonus banked on reaching it */
export const ENDLESS_TIERS: EndlessTierDef[] = [
  { name: 'GLINT', at: 300, shards: 30 },
  { name: 'KINDLE', at: 600, shards: 60 },
  { name: 'CORONA', at: 900, shards: 100 },
  { name: 'UMBRA', at: 1200, shards: 150 },
  { name: 'PARAGON', at: 1800, shards: 250 },
];
/** past the last named tier: another LIMITLESS tier every ENDLESS_TAIL_GAP s, each worth ENDLESS_TAIL_SHARDS */
export const ENDLESS_TAIL_NAME = 'LIMITLESS', ENDLESS_TAIL_GAP = 900, ENDLESS_TAIL_SHARDS = 400;

/** endless seconds at which tier n (1-based) is reached */
export function endlessMilestoneAt(n: number): number {
  if (n <= 0) return 0;
  const T = ENDLESS_TIERS;
  return n <= T.length ? T[n - 1].at : T[T.length - 1].at + (n - T.length) * ENDLESS_TAIL_GAP;
}
/** the tier reached after `sec` endless seconds (0 = none yet) */
export function endlessTierAt(sec: number): number {
  let n = 0; while (sec >= endlessMilestoneAt(n + 1)) n++; return n;
}
export const endlessTierName = (n: number): string => (n <= 0 ? '' : n <= ENDLESS_TIERS.length ? ENDLESS_TIERS[n - 1].name : ENDLESS_TAIL_NAME);
export const endlessTierShards = (n: number): number => (n <= 0 ? 0 : n <= ENDLESS_TIERS.length ? ENDLESS_TIERS[n - 1].shards : ENDLESS_TAIL_SHARDS);
/** total bonus banked by reaching tier n */
export function endlessBankFor(n: number): number { let s = 0; for (let k = 1; k <= n; k++) s += endlessTierShards(k); return s; }
const ROMAN = ['', 'I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X'];
export const roman = (n: number): string => ROMAN[n] ?? String(n);
/** "TIER III · CORONA" */
export const endlessTierLabel = (n: number): string => (n <= 0 ? '' : `TIER ${roman(n)} · ${endlessTierName(n)}`);

// ---- endless-only escalation ----
/** elite packs: from this tier, a pack of elites arrives together every elitePackGap s (first one ELITE_PACK_FIRST s after
 *  the tier is reached) */
export const ELITE_PACK_TIER = 2, ELITE_PACK_FIRST = 15;
export const elitePackGap = (tier: number): number => (tier >= 4 ? 30 : 45);
export const elitePackSize = (tier: number): number => Math.min(8, 2 + tier);
/** twin guardians: from this tier each endless guardian arrives with a second, giant elite partner */
export const TWIN_GUARDIAN_TIER = 3;
/** seconds between the milestone banner and the Apex Boon pick opening (lets the banner read first) */
export const APEX_DELAY = 2.5;

// ---- Apex Boons: one strong pick per milestone, to help survive the escalation ----
export type ApexId = 'current' | 'lattice' | 'thread' | 'pulse';
export interface ApexInfo { id: ApexId; name: string; desc: string; icon: string }
export const APEX_BOONS: Record<ApexId, ApexInfo> = {
  current: { id: 'current', name: 'Apex Current', desc: '+50% damage for the rest of the run (stacks).', icon: '⚡' },
  lattice: { id: 'lattice', name: 'Ironbound Lattice', desc: 'Take 30% less damage for the rest of the run (stacks), and fully heal.', icon: '⛨' },
  thread: { id: 'thread', name: 'Undying Thread', desc: 'Gain a revive and +40 max health.', icon: '✚' },
  pulse: { id: 'pulse', name: 'Shatter Pulse', desc: 'Shatter every ordinary foe on the field, then foes crawl for 6 seconds.', icon: '✺' },
};
export const APEX_DMG = 1.5, APEX_ARMOR = 0.7, APEX_HP = 40, APEX_STILL = 6;
